import React from 'react';

import { Box, Button, H1, HStack, Icon, Text, VStack } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { Form, Formik } from 'formik';

import { FormikFileUpload } from '../../formik/FormikFileUpload';
import { CustomFundLineupLayout } from '../CustomFundLineupLayout';

interface UploadCsvFormValues {
  fundLineupFile: File | null;
}

export const UploadCsvStep: React.FC = () => {
  const initialValues: UploadCsvFormValues = {
    fundLineupFile: null,
  };

  const handleSubmit = (_values: UploadCsvFormValues) => {};

  return (
    <CustomFundLineupLayout>
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        <Form style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box css={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 'space.600' }}>
            <VStack css={{ width: '584px', gap: 'space.400' }}>
              <H1 variant="title400">Create a custom fund lineup</H1>

              <Box
                css={{
                  border: '1px solid',
                  borderColor: 'border.default',
                  borderRadius: 'medium',
                  p: 'space.300',
                  bg: 'background.surface',
                }}
              >
                <VStack css={{ gap: 'space.300' }}>
                  <Text variant="text100" css={{ fontWeight: 500 }}>
                    Instructions
                  </Text>
                  <VStack css={{ gap: 'space.150' }}>
                    <Box>
                      <ol style={{ paddingLeft: '24px', margin: 0 }}>
                        <li style={{ marginBottom: '8px' }}>
                          <Text variant="text100">Download our CSV template to create your fund lineup.</Text>
                        </li>
                      </ol>
                      <HStack css={{ pl: 'space.300', mt: 'space.50' }}>
                        <SambaAutoLink to="/documents/[Plan ID_ XXXX] _ [Shrink ID_ xxxx] _ New QDIA & Fund Menu Submission  - Ticker List.csv">
                          <HStack css={{ gap: 'space.100', alignItems: 'center' }}>
                            <Icon name="Download" size="small" />
                            <Text as="span">Fund lineup template</Text>
                          </HStack>
                        </SambaAutoLink>
                      </HStack>
                    </Box>

                    <Box>
                      <ol start={2} style={{ paddingLeft: '24px', margin: 0 }}>
                        <li style={{ marginBottom: '8px' }}>
                          <Text variant="text100">
                            Fill out this fund lineup template with only supported tickers from Betterment's approved
                            list of mutual funds and ETFs.
                          </Text>
                        </li>
                      </ol>
                      <HStack css={{ pl: 'space.300', mt: 'space.50' }}>
                        <SambaAutoLink to="/documents/Advised 401(k) Supported Funds  - Mutual Funds.csv">
                          <HStack css={{ gap: 'space.100', alignItems: 'center' }}>
                            <Icon name="Download" size="small" />
                            <Text as="span">Betterment's approved tickers list</Text>
                          </HStack>
                        </SambaAutoLink>
                      </HStack>
                    </Box>

                    <Box>
                      <ol start={3} style={{ paddingLeft: '24px', margin: 0 }}>
                        <li>
                          <Text variant="text100">Upload the finalized fund lineup here.</Text>
                        </li>
                      </ol>
                    </Box>
                  </VStack>

                  <Box css={{ bg: 'primitives.grey.10', borderRadius: 'medium', p: 'space.200' }}>
                    <VStack css={{ gap: 'space.50' }}>
                      <Text variant="text100" css={{ fontWeight: 500 }}>
                        Guidelines
                      </Text>
                      <ul style={{ paddingLeft: '24px', margin: 0 }}>
                        <li style={{ marginBottom: '4px' }}>
                          <Text variant="text100">
                            Do not modify the template's format. Add tickers to the fund lineup in column A. Designate a
                            ticker as a default investment by writing "QDIA" in column B.
                          </Text>
                        </li>
                        <li style={{ marginBottom: '4px' }}>
                          <Text variant="text100">
                            We require a minimum of one Target Date Fund to be designated by default. Participants can
                            select any mix of funds within your specified lineup, though.
                          </Text>
                        </li>
                        <li>
                          <Text variant="text100">
                            If you do not see a ticker you wish to add or need support, you may{' '}
                            <SambaAutoLink to="/support" target="_blank" rel="noopener noreferrer">
                              contact us
                            </SambaAutoLink>
                            .
                          </Text>
                        </li>
                      </ul>
                    </VStack>
                  </Box>
                </VStack>
              </Box>

              <Box
                css={{
                  border: '1px solid',
                  borderColor: 'border.default',
                  borderRadius: 'medium',
                  p: 'space.300',
                  bg: 'background.surface',
                }}
              >
                <VStack css={{ gap: 'space.150' }}>
                  <Text variant="text100">Upload fund lineup CSV</Text>
                  <FormikFileUpload name="fundLineupFile" acceptedFileTypes=".csv" />
                </VStack>
              </Box>

              <Button type="submit" variant="solidPrimary">
                Continue
              </Button>
            </VStack>
          </Box>
        </Form>
      </Formik>
    </CustomFundLineupLayout>
  );
};
